import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatCurrency } from '../Help/helper';
import MoonLoader from 'react-spinners/MoonLoader';
import RequisitionDetails from './RequisitionDetails';
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, EyeIcon } from '@heroicons/react/24/outline';

const ProcurementsTable = () => {
  const [procurements, setProcurements] = useState([]);
  const [filteredProcurements, setFilteredProcurements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProcurement, setSelectedProcurement] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const openModal = (procurement) => {
    setSelectedProcurement(procurement);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProcurement(null);
  };

  useEffect(() => {
    const fetchProcurements = async () => {
      setIsLoading(true);
      const q = query(collection(db, 'procurements'), where('status', '==', 'Requested'));
      const querySnapshot = await getDocs(q);
      const fetchedProcurements = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setProcurements(fetchedProcurements);
      setIsLoading(false);
    };

    fetchProcurements();
  }, []);

  useEffect(() => {
    const filtered = procurements.filter((procurement) => {
      const query = searchQuery.toLowerCase();
      return (
        procurement.procurementNumber.toLowerCase().includes(query) ||
        procurement.customerName.toLowerCase().includes(query) ||
        procurement.category.toLowerCase().includes(query) ||
        procurement.supplier.toLowerCase().includes(query)
      );
    });
    setFilteredProcurements(filtered);
    setCurrentPage(1);
  }, [searchQuery, procurements]);

  const sortedProcurements = React.useMemo(() => {
    let sortableProcurements = [...filteredProcurements];
    if (sortConfig.key !== '') {
      sortableProcurements.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'grandTotal') {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        } else {
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableProcurements;
  }, [filteredProcurements, sortConfig]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProcurements = sortedProcurements.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedProcurements.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }

    if (sortConfig.direction === 'ascending') {
      return <span className="ml-1">&#9650;</span>;
    } else {
      return <span className="ml-1">&#9660;</span>;
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-xs text-gray-600">
              All pending requisitions that require approval
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              className="block w-full pl-10 pr-3 py-2 text-sm border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <MoonLoader size={50} color="#9333EA" />
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-purple-600 to-indigo-600">
                <tr>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-white cursor-pointer hover:bg-purple-700"
                    onClick={() => requestSort('procurementNumber')}
                  >
                    Procurement Number {getSortIcon('procurementNumber')}
                  </th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-white cursor-pointer hover:bg-purple-700"
                    onClick={() => requestSort('customerName')}
                  >
                    Customer Name {getSortIcon('customerName')}
                  </th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-white cursor-pointer hover:bg-purple-700"
                    onClick={() => requestSort('category')}
                  >
                    Category {getSortIcon('category')}
                  </th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-white cursor-pointer hover:bg-purple-700"
                    onClick={() => requestSort('supplier')}
                  >
                    Supplier {getSortIcon('supplier')}
                  </th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-white cursor-pointer hover:bg-purple-700"
                    onClick={() => requestSort('grandTotal')}
                  >
                    Grand Total {getSortIcon('grandTotal')}
                  </th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-white cursor-pointer hover:bg-purple-700"
                    onClick={() => requestSort('dateRequired')}
                  >
                    Date Required {getSortIcon('dateRequired')}
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-bold text-white uppercase">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {currentProcurements.map((procurement) => (
                  <tr key={procurement.id} className="hover:bg-purple-50 transition-colors">
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs font-semibold text-gray-900">
                      {procurement.procurementNumber}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs text-gray-600">
                      {procurement.customerName}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs uppercase text-gray-600">
                      {procurement.category}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs text-gray-600">
                      {procurement.supplier}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs font-bold text-gray-900">
                      {formatCurrency(procurement.grandTotal)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-xs text-gray-600">
                      {procurement.dateRequired}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-center">
                      <button
                        onClick={() => openModal(procurement)}
                        className="inline-flex items-center px-3 py-1 text-xs font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl hover:from-purple-600 hover:to-indigo-700 shadow-md transition-all duration-200"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!isLoading && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div>
              <p className="text-xs text-gray-700">
                Showing <span className="font-semibold">{indexOfFirstItem + 1}</span> to{' '}
                <span className="font-semibold">
                  {Math.min(indexOfLastItem, sortedProcurements.length)}
                </span>{' '}
                of <span className="font-semibold">{sortedProcurements.length}</span> results
              </p>
            </div>
            <div>
              <nav className="inline-flex -space-x-px rounded-xl shadow-md" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 text-xs font-semibold text-gray-500 bg-white border border-gray-300 rounded-l-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                </button>
                {[...Array(totalPages).keys()].map((number) => (
                  <button
                    key={number + 1}
                    onClick={() => setCurrentPage(number + 1)}
                    className={`relative inline-flex items-center px-3 py-2 border text-xs font-semibold ${
                      currentPage === number + 1
                        ? 'z-10 bg-gradient-to-r from-purple-500 to-indigo-600 border-purple-600 text-white'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {number + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 text-xs font-semibold text-gray-500 bg-white border border-gray-300 rounded-r-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && selectedProcurement && (
        <RequisitionDetails procurementId={selectedProcurement.id} onClose={closeModal} />
      )}
    </div>
  );
};

export default ProcurementsTable;
